import * as React from 'react';
import { useCallback, useState } from 'react';
import { DataTable } from 'ui-components/table/data-table/data-table';
import styles from './requests-table.module.scss';
import {
    ThreeDotsMenu,
    ThreeDotsMenuItem,
    ThreeDotsMenuItemText,
} from 'ui-components/three-dots-menu';
import { Tooltip } from 'ui-components/tooltip/tooltip';
import {
    OrderManagementRequest,
    REQUEST_TABLE_IN_PROGRESS_TAB_NAME,
    REQUEST_TABLE_MY_REPORTS_TAB_NAME,
    REQUEST_TABLE_SHARED_WITH_ME_TAB_NAME,
    REQUEST_TABLE_ADMIN_VIEW_TAB_NAME,
    TableActiveTab,
} from 'core/entities/offline-reports/offline-reports';

import EmptyTable from '../empty-table/empty-table';
import moment from 'moment';
import { ReportTypeComponent } from 'features/offline-reports/order-management/order-management';
import { ReportMaterialsComponent } from 'features/offline-reports/order-management/components/report-materials/report-materials';
import { SharingDetails } from 'features/offline-reports/order-management/components/report-sharing-details/report-sharing-details';
import { SharingComponent } from 'features/offline-reports/order-management/components/sharing/sharing';
import { PlacerLink } from 'ui-components/placer-link/placer-link';
import { ColumnsType } from 'antd/es/table';
import { useExtraReportsPermissions } from 'features/offline-reports/xtra-iframe/hooks/use-offline-report-permissions';

export interface RequestsTableProps {
    searchValue: string;
    requests: OrderManagementRequest[];
    setRequestId: (value: string | null) => void;
    showMaterials?: boolean;
    showAccountMaterials?: boolean;
    showShare?: boolean;
    load?: boolean;
    activeTab: TableActiveTab;
}

export interface RecordType extends Omit<OrderManagementRequest, 'reportDates'> {
    key: number;
    requestedOn: string;
    reportDates: string;
    requestedOnFullDate: string;
}

const myReportsExclusionTabList = ['sharedBy', 'requestedBy', 'status'];
const sharedWithMeExclusionTabList = ['requestedOn', 'sharing', 'action', 'requestedBy', 'status'];
const adminViewExclusionTabList = ['sharedBy'];
const inProgressExclusionTabList = [
    'sharing',
    'sharedBy',
    'reportMaterials',
    'requestedBy',
    'status',
];

const getColumnsMetaDataDetails = (
    setRequestId: RequestsTableProps['setRequestId'],
    pathName: string,
    hasXtraRequestsTablePermission: boolean,
    showMaterials?: boolean,
    showAccountMaterials?: boolean,
    hoveringRecordId?: number,
): ColumnsType<RecordType> => [
    {
        title: 'Report type',
        dataIndex: 'reportName',
        key: 'reportName',
        width: showMaterials ? '34%' : showAccountMaterials ? '28%' : '46%',
        ellipsis: {
            showTitle: false,
        },
        render: (_, record) => {
            return <ReportTypeComponent key={record.key} record={record} />;
        },
    },
    {
        title: 'Report Dates',
        dataIndex: 'reportDates',
        key: 'reportType',
        width: showMaterials ? '190px' : showAccountMaterials ? '120px' : '240px',
        className: styles.alignContentRight,
        render: (_, record: RecordType) => {
            return <div className={styles.dates}>{record.reportDates}</div>;
        },
    },
    {
        title: 'Shared By',
        dataIndex: 'sharedBy',
        key: 'sharedBy',
        width: '180px',
        className: styles.alignContentRight,
        render: (_, record: RecordType) => {
            return <SharingDetails record={record} />;
        },
    },
    {
        title: 'Requested On',
        dataIndex: 'requestedOn',
        key: 'requestedOn',
        width: showMaterials ? '140px' : showAccountMaterials ? '90px' : '220px',
        className: styles.alignContentRight,
        sorter: (itemA, itemB) => {
            return moment(itemA.requestedOnFullDate).isBefore(moment(itemB.requestedOnFullDate))
                ? 1
                : -1;
        },
        render: (_, record) => {
            return (
                <Tooltip title="View Request">
                    <div className={styles.dates}>{record.requestedOn}</div>
                    {hasXtraRequestsTablePermission && record.status === 'new' && (
                        <div className={styles.inProgressRequestedOnText}>In Progress</div>
                    )}
                </Tooltip>
            );
        },
    },
    {
        title: 'Requested By',
        dataIndex: 'requestedBy',
        key: 'requestedBy',
        width: '80px',
        className: styles.alignContentRight,
        render: (_, record) => {
            return (
                <Tooltip title={record.requestorEmail}>
                    <div>{record.requestorEmail}</div>
                </Tooltip>
            );
        },
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: '80px',
        className: styles.alignContentRight,
        render: (_, record) => {
            const closedRequestsPossibleStatus = ['closed', 'solved'];
            const completed = 'Completed';
            const inProgress = 'In Progress';

            return (
                <Tooltip title="Status">
                    <div>
                        {closedRequestsPossibleStatus.includes(record.status)
                            ? completed
                            : inProgress}
                    </div>
                </Tooltip>
            );
        },
    },
    {
        title: 'Shared With',
        dataIndex: 'sharing',
        key: 'sharing',
        width: '80px',
        className: styles.alignContentRight,
        render: (_, record) => {
            return <SharingComponent record={record} hoveringRecordId={hoveringRecordId} />;
        },
    },
    {
        title: 'Actions',
        dataIndex: 'reportMaterials',
        key: 'reportMaterials',
        width: '80px',
        className: styles.alignContentRight,
        render: (_, record) => {
            return <ReportMaterialsComponent record={record} />;
        },
    },
    {
        title: '',
        dataIndex: 'action',
        width: '50px',
        key: 'action',
        align: 'right' as 'right',
        render: (_, record) => (
            <ThreeDotsMenu>
                <ThreeDotsMenuItem
                    {...{
                        onClick: () => {},
                    }}
                    className={styles.actionItem}
                >
                    <ThreeDotsMenuItemText>
                        <PlacerLink
                            to={{
                                pathname: record.reportId ? `${pathName}${record.reportId}` : '#',
                            }}
                        >
                            View on Catalog
                        </PlacerLink>
                    </ThreeDotsMenuItemText>
                </ThreeDotsMenuItem>
                <ThreeDotsMenuItem
                    className={styles.actionItem}
                    {...{
                        onClick: () => {
                            setRequestId(String(record.id));
                        },
                    }}
                >
                    <ThreeDotsMenuItemText>View Request</ThreeDotsMenuItemText>
                </ThreeDotsMenuItem>
            </ThreeDotsMenu>
        ),
    },
];

export const RequestsTable: React.FunctionComponent<RequestsTableProps> = ({
    requests,
    setRequestId,
    searchValue,
    showMaterials,
    showAccountMaterials,
    activeTab,
}) => {
    const [hoveringRecordId, setHoveringRecordId] = useState<number>(0);
    const getPathName = () => {
        const sessionStoragePathItem = sessionStorage.getItem('sourceFeatureName');

        if (sessionStoragePathItem && sessionStoragePathItem.includes('advanced-reports')) {
            return '/advanced-reports/placer-xtra/';
        }

        return '/insights/placer-xtra/';
    };
    const listToDisplay = requests
        .filter(
            (request) =>
                request.reportName &&
                request.reportName.toLowerCase().includes(searchValue.toLowerCase()),
        )
        .sort((itemA, itemB) => {
            return moment(itemA.requestedOn).isBefore(moment(itemB.requestedOn)) ? 1 : -1;
        });

    const pathName = getPathName();

    const { hasExtraRevampPermission, hasReportSharingPermission, hasXtraRequestsTablePermission } =
        useExtraReportsPermissions();

    const formatColumnsMetaDataByActiveTab = useCallback(
        (columnsMetaData: ColumnsType<RecordType>): ColumnsType<RecordType> => {
            const exclusionColumnPerTab = {
                [REQUEST_TABLE_MY_REPORTS_TAB_NAME]: myReportsExclusionTabList,
                [REQUEST_TABLE_SHARED_WITH_ME_TAB_NAME]: sharedWithMeExclusionTabList,
                [REQUEST_TABLE_ADMIN_VIEW_TAB_NAME]: adminViewExclusionTabList,
            };

            if (!hasXtraRequestsTablePermission) {
                exclusionColumnPerTab[REQUEST_TABLE_IN_PROGRESS_TAB_NAME] =
                    inProgressExclusionTabList;
            }

            if (!hasExtraRevampPermission) {
                exclusionColumnPerTab[activeTab].push('reportMaterials');
            }
            if (!hasReportSharingPermission) {
                exclusionColumnPerTab[activeTab].push('sharing');
            }
            const activeTabColumnExclusionList = exclusionColumnPerTab[activeTab];

            activeTabColumnExclusionList?.forEach((columnExclude) => {
                const removeIndex = columnsMetaData
                    .map((column) => column.key)
                    .indexOf(columnExclude);

                removeIndex !== -1 && columnsMetaData.splice(removeIndex, 1);
            });
            return columnsMetaData;
        },
        [
            activeTab,
            hasExtraRevampPermission,
            hasReportSharingPermission,
            hasXtraRequestsTablePermission,
        ],
    );

    const getColumnsMeta = (
        setRequestId: RequestsTableProps['setRequestId'],
        pathName: string,
        hoveringRecordId: number,
    ): ColumnsType<RecordType> => {
        let columnsMetaData = getColumnsMetaDataDetails(
            setRequestId,
            pathName,
            hasXtraRequestsTablePermission,
            showMaterials,
            showAccountMaterials,
            hoveringRecordId,
        );

        columnsMetaData = formatColumnsMetaDataByActiveTab(columnsMetaData);
        return columnsMetaData;
    };

    return (
        <div>
            <DataTable
                locale={{
                    emptyText: <EmptyTable activeTab={activeTab} />,
                }}
                className={styles.dataTable}
                rowClassName={styles.dataTableRow}
                dataSource={listToDisplay.map((request): RecordType => {
                    return {
                        ...request,
                        key: request.id,
                        requestedOn: moment(request.requestedOn).format('MMM DD, YYYY'),
                        requestedOnFullDate: request.requestedOn,
                        reportDates: `${moment(request.reportDates.from).format(
                            'MMM DD',
                        )} - ${moment(request.reportDates.to).format('MMM DD, YYYY')}`,
                    };
                })}
                columns={getColumnsMeta(setRequestId, pathName, hoveringRecordId)}
                onRow={(record: RecordType) => {
                    return {
                        onClick: () => {
                            setRequestId(String(record.id));
                        },
                        onMouseOver: () => {
                            setHoveringRecordId(record.id);
                        },
                        onMouseLeave: () => {
                            setHoveringRecordId(0);
                        },
                    };
                }}
            />
        </div>
    );
};

export default RequestsTable;
